import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, CSSProperties, PropsWithChildren } from 'react';
import clsx from 'clsx';
import { cn } from '../../utils/cn';
import * as Slot from '../Slot';

const variants = cva( [
  'relative flex font-onest overflow-hidden group/button cursor-pointer text-center',
  'disabled:pointer-events-none'
], {
  variants: {
    variant: {
      primary: [
        'text-white font-bold',
        'before:absolute before:inset-0 before:bg-gradient-to-r before:bg-cover before:from-[#6739FF] before:to-[#6E52C3] before:rounded-full',
        'after:absolute after:inset-0 after:bg-gradient-to-r after:opacity-0 hover:after:opacity-100 after:transition-opacity after:duration-200 after:bg-cover',
        'after:from-[#6739FF] after:from-60% after:to-[#6E52C3] after:rounded-full active:after:from-[#6739FF] active:after:to-[#6739FF]',
        'disabled:before:from-[#D5D3D3] disabled:before:to-[#9A9A9A]'
      ],
      secondary: [
        'font-bold',
        'before:absolute before:inset-0 before:bg-gradient-to-r before:bg-cover before:from-white before:from-0% dark:before:from-[#221A21] before:to-[#EDECFF] dark:before:to-[#171844] before:border-2 before:border-[#6E55D9] before:rounded-full',
        'after:from-white after:from-50% dark:after:from-[#221A21] dark:after:from-50% after:to-[#EDECFF] dark:after:to-[#171844] after:border-2 after:border-[#6E55D9] after:rounded-full active:after:from-white active:after:to-white dark:active:after:to-[#131313] dark:active:after:from-[#131313]',
        'after:absolute after:inset-0 after:bg-gradient-to-r after:opacity-0 hover:after:opacity-100 after:transition-opacity after:duration-200 after:bg-cover',
        'disabled:before:from-[#D5D3D3] disabled:before:to-[#9A9A9A] disabled:before:border-[#656565] dark:disabled:before:from-[#585656] dark:disabled:before:to-[#585656]'
      ],
      shimmer: [
        'text-white font-bold',
        'before:absolute before:inset-0 before:bg-gradient-to-r before:bg-cover before:from-[#6739FF] before:to-[#6E52C3] before:rounded-full',
        'after:from-[#6739FF] after:from-60% after:to-[#6E52C3] active:after:from-[#6739FF] active:after:to-[#6739FF] after:rounded-full',
        'after:absolute after:inset-0 after:bg-gradient-to-r after:opacity-0 hover:after:opacity-100 after:transition-opacity after:duration-200 after:bg-cover',
        'disabled:before:from-[#D5D3D3] disabled:before:to-[#9A9A9A]'
      ],
      link: [ 'font-semibold !px-0 !py-0' ],
      destructive: [
        'text-white font-bold',
        'before:absolute before:inset-0 before:bg-gradient-to-r before:bg-cover before:from-[#FF3C3C] before:to-[#F00] before:rounded-full',
        'after:absolute after:inset-0 after:bg-gradient-to-r after:opacity-0 hover:after:opacity-100 after:transition-opacity after:duration-200 after:bg-cover',
        'after:from-[#FF3C3C] after:from-60% after:to-[#F00] after:rounded-full active:after:from-[#FF3C3C] active:after:to-[#FF3C3C]',
        'disabled:before:from-[#D5D3D3] disabled:before:to-[#9A9A9A]'
      ]
    },
    size: {
      default: 'text-sm px-6 py-[15px] md:px-4 md:py-[11.5px]',
      large: 'text-sm px-6 py-[14.5px] md:py-[14.5px]',
      sm: 'text-sm px-3.5 pt-[8px] md:py-[8.5px]',
    } }
} );

type ButtonProps =
  ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof variants> &
  PropsWithChildren<{
    isLoading?: boolean;
    asChild?: boolean;
  }>;

export const Button = ( { children, variant = 'primary', size = 'default', asChild = false, className, ...buttonProps }: ButtonProps ) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      className={ cn( variants( { variant, size } ), className ) }
      { ...buttonProps }
    >
      <Slot.Slottable child={ children }>
        { ( child ) => (
          <span
            className={ clsx(
              'relative z-10 text-nowrap mx-auto flex flex-row items-center gap-2 leading-normal font-onest',
              {
                'bg-clip-text text-transparent bg-gradient-to-r from-[#AA5297] to-[#4D55FC] dark:from-[#F7F2F9] dark:to-[#B8BBFD] dark:group-hover/button:from-[#DCBAD6] dark:group-hover/button:to-[#B8BBFD] dark:group-active/button:from-[#CA97C1] dark:group-active/button:to-[#9499FD] group-disabled/button:text-white': variant === 'secondary',
                'bg-clip-text text-transparent bg-gradient-to-r from-[#AA5297] to-[#4D55FC] hover:from-[#4D55FC] hover:to-[#4D55FC] dark:from-[#C4527A] dark:to-[#FB513E] group-disabled/button:bg-transparent group-disabled/button:text-[#595656] dark:hover:from-[#FB513E] dark:hover:to-[#FB513E] dark:active:from-[#C4527A] dark:active:to-[#FB513E]': variant === 'link',
                'text-shimmer text-white/65 group-disabled/button:text-white': variant === 'shimmer',
              },
            ) }
            style={ {
              '--text-length': children?.toString().length,
              textShadow: '0px 2px 2px rgba(0,0,0,0.01)',
            } as CSSProperties }
          >
            { child }
          </span>
        ) }
      </Slot.Slottable>
    </Comp>
  )
}
Button.displayName = 'Button';
